import { runMarketingTrackingEvent } from './thirdPartyTracking';

/**
 * Meta Pixel helper — thin wrapper around the global fbq function.
 * Only fires on production hosts to keep test/preview traffic out of real pixel data.
 *
 * Funnel event map:
 *   Landing page load  → ViewContent + PageView (auto)
 *   CTA click          → Lead
 *   Email confirmed    → CompleteRegistration
 *   Stripe opens       → InitiateCheckout
 *   Stripe succeeds    → Subscribe
 */

const PRODUCTION_HOSTS = ['lovibook.com', 'www.lovibook.com'];

function isProductionHost() {
  return typeof window !== 'undefined' && PRODUCTION_HOSTS.includes(window.location.hostname);
}

function fbq(command: string, ...args: unknown[]) {
  if (!isProductionHost()) return Promise.resolve();
  return runMarketingTrackingEvent(() => {
    if (typeof (window as Window & { fbq?: unknown }).fbq === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).fbq(command, ...args);
    }
  });
}

/** Fire on every client-side route change. */
export function trackPageView() {
  return fbq('track', 'PageView');
}

/** Fire on landing page load — explicit high-intent signal, enables better retargeting audiences. */
export function trackViewContent(contentName: string) {
  return fbq('track', 'ViewContent', { content_name: contentName });
}

/** Fire when a visitor clicks a sign-up CTA — signals purchase intent. */
export function trackLead() {
  return fbq('track', 'Lead', { currency: 'USD', value: 0, content_name: 'Sign Up CTA' });
}

/**
 * Fire when the user confirms their email — correct stage for CompleteRegistration per Meta spec.
 * eventID enables deduplication with Conversions API.
 */
export function trackCompleteRegistration(eventId?: string) {
  return fbq('track', 'CompleteRegistration', {
    currency: 'USD',
    value: 0,
    status: true,
    content_name: 'LoviBook Free Registration',
  }, eventId ? { eventID: eventId } : undefined);
}

/**
 * Fire when the user enters Stripe checkout — enables checkout abandonment retargeting.
 * planPrice should be the actual plan price (e.g. 9.99).
 */
export function trackInitiateCheckout(planName: string, planPrice: number, eventId?: string) {
  return fbq('track', 'InitiateCheckout', {
    currency: 'USD',
    value: planPrice,
    num_items: 1,
    content_name: planName,
  }, eventId ? { eventID: eventId } : undefined);
}

/**
 * Fire after Stripe subscription payment succeeds — the primary paid conversion event.
 * value = actual first-payment amount; predicted_ltv = conservative 12-month estimate.
 */
export function trackSubscribe(planName: string, planPrice: number, eventId?: string) {
  return fbq('track', 'Subscribe', {
    currency: 'USD',
    value: planPrice,
    predicted_ltv: Math.round(planPrice * 12 * 100) / 100,
    content_name: planName,
  }, eventId ? { eventID: eventId } : undefined);
}
